import { useCallback, useEffect, useMemo, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { addDays, format, startOfDay, subDays } from "date-fns";
import { pl } from "date-fns/locale";
import {
  canNavigateNext,
  canNavigatePrevious,
  getInitialAnchorDate,
  getScheduleDateRange,
} from "./swimmingDateUtils";

interface UseSwimmingDateNavigationOptions {
  isWeekly: boolean;
  minDate?: Date;
  maxDate?: Date;
}

export default function useSwimmingDateNavigation({
  isWeekly,
  minDate,
  maxDate,
}: UseSwimmingDateNavigationOptions) {
  const isMobile = useMediaQuery("(max-width: 980px)");
  const effectiveMin = useMemo(() => startOfDay(minDate ?? new Date()), [minDate]);

  const [anchorDate, setAnchorDate] = useState<Date>(() => getInitialAnchorDate(minDate, maxDate));

  useEffect(() => {
    setAnchorDate(getInitialAnchorDate(minDate, maxDate));
  }, [isWeekly, isMobile, minDate, maxDate]);

  const { dateFrom, dateTo } = useMemo(
    () => getScheduleDateRange(anchorDate, isWeekly, isMobile),
    [anchorDate, isWeekly, isMobile],
  );

  const canGoPrevious = useMemo(
    () => canNavigatePrevious(anchorDate, effectiveMin, isWeekly, isMobile),
    [anchorDate, effectiveMin, isWeekly, isMobile],
  );

  const canGoNext = useMemo(
    () => canNavigateNext(anchorDate, maxDate, isWeekly, isMobile),
    [anchorDate, maxDate, isWeekly, isMobile],
  );

  const handlePreviousPeriod = useCallback(() => {
    setAnchorDate((prev) => (isWeekly ? subDays(prev, isMobile ? 1 : 7) : subDays(prev, 1)));
  }, [isWeekly, isMobile]);

  const handleNextPeriod = useCallback(() => {
    setAnchorDate((prev) => (isWeekly ? addDays(prev, isMobile ? 1 : 7) : addDays(prev, 1)));
  }, [isWeekly, isMobile]);

  const handleDateChange = useCallback((date: Date) => {
    setAnchorDate(startOfDay(date));
  }, []);

  const dateHeading = useMemo(() => {
    if (!isWeekly) {
      return format(anchorDate, "EEEE, d MMMM yyyy", { locale: pl });
    }

    if (isMobile) {
      return format(anchorDate, "EEEE, d MMMM yyyy", { locale: pl });
    }

    const endDate = addDays(anchorDate, 6);
    return `${format(anchorDate, "dd.MM")} – ${format(endDate, "dd.MM.yyyy")}`;
  }, [anchorDate, isWeekly, isMobile]);

  const dateRangeLabel = useMemo(() => {
    if (!isWeekly) return format(anchorDate, "dd MMM yyyy", { locale: pl });
    if (isMobile) return format(anchorDate, "dd MMM", { locale: pl });
    const endDate = addDays(anchorDate, 6);
    return `${format(anchorDate, "dd.MM")} - ${format(endDate, "dd.MM.yyyy")}`;
  }, [anchorDate, isWeekly, isMobile]);

  return {
    isMobile,
    anchorDate,
    dateFrom,
    dateTo,
    canGoPrevious,
    canGoNext,
    handlePreviousPeriod,
    handleNextPeriod,
    handleDateChange,
    dateHeading,
    dateRangeLabel,
  };
}
